import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageSearch } from '../models/message-search-model';


@Injectable({
  providedIn: 'root'
})
export class MessageSearchService {

  private API_MESSAGE_SEARCH_URL = environment.ipserver + 'o2c-backend/private/messages/search';


  /**
   * Url to update message status to processed. Backend receives body: JSON array of message IDs (e.g. [123] or [1, 2, 3]).
   */
  private API_UPDATE_MESSAGE_STATUS_URL = environment.ipserver + 'o2c-backend/private/messages/process';

  /**
   * Url to update MESSAGE reference ID
   */
  private API_UPDATE_REFERENCE_ID_URL = environment.ipserver + 'o2c-backend/private/messages/updateReferenceId';

  /**
   * Url to get messages by reference ID (invoice, order, po)
   */
  private API_GET_BY_REFERENCE_ID_URL = environment.ipserver + 'o2c-backend/private/messages/getByReferenceId';


  constructor(private httpClient: HttpClient) {

  }

  /**
     * Generates a report with credits registered in DB
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-14
     * @return Observable with an array that contains an invoicing attached documents
     */
  public list(filter: any ): Observable<Array<MessageSearch>> {
    const paramsForReq = new HttpParams()
      .set('receivedDateFrom', filter.startDate)
      .set('receivedDateTo', filter.endDate)
      .set('customerNumber', filter.customerNumber)
      .set('businessAnalyst', filter.businessAnalyst || '')
      .set('invoiceNumber', filter.invoiceNumber || '')
      .set('poNumber', filter.poNumber || '')
      .set('orderNumber', filter.orderNumber || '')
      .set('messageNumber', filter.messageNumber || '')
      .set('status', filter.status || '');
    return this.httpClient.get<Array<MessageSearch>>(this.API_MESSAGE_SEARCH_URL, {params: paramsForReq});
  }

  /**
   * Mark one or more messages as processed. Body is a JSON array of message IDs.
   * Backend may return { processedCount: number } or another format; 2xx without error is treated as success.
   */
  public processMessages(messageIds: number[]): Observable<{ processedCount: number }> {
    return this.httpClient.put<any>(this.API_UPDATE_MESSAGE_STATUS_URL, messageIds).pipe(
      map((res) => {
        if (res != null && typeof res.processedCount === 'number') {
          return { processedCount: res.processedCount };
        }
        return { processedCount: messageIds.length };
      })
    );
  }

  /**
   * Method to update the status of a single EDI Notification (uses same endpoint with body [messageId])
   */
  public updateStatusNotification(messageId: number): Observable<boolean> {
    return this.processMessages([messageId]).pipe(
      map((res) => res.processedCount > 0)
    );
  }

  /**
   * Update the reference ID of a message
   * @param messageId The ID of the message to update
   * @param referenceId The new reference ID (document number) to assign
   * @return Observable with the result of the operation
   */
  public updateReferenceId(messageId: number, referenceId: string): Observable<any> {
    const paramsForReq = new HttpParams()
      .set('messageId', messageId.toString())
      .set('referenceId', referenceId);
    return this.httpClient.put(this.API_UPDATE_REFERENCE_ID_URL, '', { params: paramsForReq });
  }

  /**
   * Get messages by reference ID (invoice number, order number, or PO number)
   * @param invoiceNumber The invoice number to search
   * @param orderNumber The order number to search
   * @param poNumber The PO number to search
   * @return Observable with an array of messages
   */
  public getByReferenceId(invoiceNumber: string, orderNumber: string, poNumber: string): Observable<Array<MessageSearch>> {
    const paramsForReq = new HttpParams()
      .set('invoiceNumber', invoiceNumber || '')
      .set('orderNumber', orderNumber || '')
      .set('poNumber', poNumber || '');
    return this.httpClient.get<Array<MessageSearch>>(this.API_GET_BY_REFERENCE_ID_URL, { params: paramsForReq });
  }


}
